import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { getBooks } from "../api";
import { getLast } from "../lib/storage";
import { getSection, SECTION_LABEL } from "../lib/bookThemes";
import { ChevronLeft } from "lucide-react";

export default function BookPicker({ open, onOpenChange, currentBook, forceBookId }) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("fw_book_sort") || "canonical");
  const [tab, setTab] = useState("OT");
  const currentBtnRef = useRef(null);

  const changeSort = (m) => { setSortMode(m); localStorage.setItem("fw_book_sort", m); };

  useEffect(() => {
    if (open && books.length === 0) getBooks().then(setBooks);
  }, [open]);

  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  // open straight into the book you're reading (or last read), showing its chapters
  useEffect(() => {
    if (!open || books.length === 0) return;
    const targetId = forceBookId || currentBook || getLast()?.book;
    const cur = targetId ? books.find((b) => b.id === targetId) : null;
    if (cur) {
      setTab(cur.testament);
      setSelected(cur);
    }
    const t = setTimeout(() => {
      currentBtnRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
    }, 120);
    return () => clearTimeout(t);
  }, [open, currentBook, forceBookId, books, sortMode]);

  const ot = books.filter((b) => b.testament === "OT");
  const nt = books.filter((b) => b.testament === "NT");

  const go = (b, c) => {
    onOpenChange(false);
    navigate(`/read/${b.id}/${c}`);
  };

  const BookList = ({ list }) => {
    if (sortMode === "az") {
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
      return (
        <div className="grid grid-cols-2 gap-2 pb-10 sm:grid-cols-3">
          {sorted.map((b) => (
            <button
              key={b.id}
              ref={currentBook === b.id ? currentBtnRef : null}
              data-testid={`book-${b.id}`}
              onClick={() => setSelected(b)}
              className={`font-ui rounded-xl border px-3 py-2.5 text-left text-sm transition-all hover:border-primary hover:bg-muted ${
                currentBook === b.id ? "border-primary bg-muted" : "border-border"
              }`}
            >
              <span className="block truncate font-medium">{b.name}</span>
              <span className="text-xs text-muted-foreground">{b.chapters} ch</span>
            </button>
          ))}
        </div>
      );
    }
    // group by section (canonical)
    const groups = {};
    list.forEach((b) => {
      const s = getSection(b.id);
      (groups[s] ||= []).push(b);
    });
    return (
      <div className="space-y-6 pb-10">
        {Object.entries(groups).map(([sec, items]) => (
          <div key={sec}>
            <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">
              {SECTION_LABEL[sec]}
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {items.map((b) => (
                <button
                  key={b.id}
                  ref={currentBook === b.id ? currentBtnRef : null}
                  data-testid={`book-${b.id}`}
                  onClick={() => setSelected(b)}
                  className={`font-ui rounded-xl border px-3 py-2.5 text-left text-sm transition-all hover:border-primary hover:bg-muted ${
                    currentBook === b.id ? "border-primary bg-muted" : "border-border"
                  }`}
                >
                  <span className="block truncate font-medium">{b.name}</span>
                  <span className="text-xs text-muted-foreground">{b.chapters} ch</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full max-w-lg overflow-y-auto" data-testid="book-picker">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl">
            {selected ? (
              <button
                onClick={() => setSelected(null)}
                className="font-ui flex items-center gap-1 text-base text-muted-foreground hover:text-foreground"
                data-testid="back-to-books"
              >
                <ChevronLeft className="h-4 w-4" /> All Books
              </button>
            ) : (
              "Choose a Book"
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Browse books of the Bible by testament and section, then pick a chapter to read.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {selected ? (
            <div>
              <h3 className="font-serif-display mb-4 text-3xl">{selected.name}</h3>
              <div className="grid grid-cols-5 gap-2 pb-10 sm:grid-cols-6">
                {Array.from({ length: selected.chapters }, (_, i) => i + 1).map((c) => {
                  const isCurrent = (currentBook || getLast()?.book) === selected.id && getLast()?.chapter === c;
                  return (
                  <button
                    key={c}
                    data-testid={`chapter-${c}`}
                    onClick={() => go(selected, c)}
                    className={`font-ui flex h-12 items-center justify-center rounded-xl border text-sm font-medium transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground ${
                      isCurrent ? "border-primary bg-primary/15 text-primary" : "border-border"
                    }`}
                  >
                    {c}
                  </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="font-ui grid w-full grid-cols-2">
                <TabsTrigger value="OT" data-testid="tab-ot">Old Testament</TabsTrigger>
                <TabsTrigger value="NT" data-testid="tab-nt">New Testament</TabsTrigger>
              </TabsList>
              <div className="mt-4 flex items-center justify-end gap-1" data-testid="book-sort-toggle">
                <span className="font-ui mr-1 text-xs text-muted-foreground">Sort:</span>
                <button
                  onClick={() => changeSort("canonical")}
                  data-testid="sort-canonical"
                  className={`font-ui rounded-full px-3 py-1 text-xs font-semibold transition-colors ${sortMode === "canonical" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}
                >
                  Canonical
                </button>
                <button
                  onClick={() => changeSort("az")}
                  data-testid="sort-az"
                  className={`font-ui rounded-full px-3 py-1 text-xs font-semibold transition-colors ${sortMode === "az" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-muted"}`}
                >
                  A–Z
                </button>
              </div>
              <TabsContent value="OT" className="mt-4">
                <BookList list={ot} />
              </TabsContent>
              <TabsContent value="NT" className="mt-4">
                <BookList list={nt} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
